import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Menu, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from '../components/ui/sheet';
import { projects, siteMeta } from '../mock';

const SCROLL_THRESHOLD_PX = 60;

const Header = ({ transparent = false }) => {
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // setScrolled is stable (from useState) and onScroll is defined inside the effect,
  // so no additional deps are needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > SCROLL_THRESHOLD_PX);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const isHome = location.pathname === '/';
  const onDark = transparent && isHome && !scrolled;

  const colorCls = onDark ? 'text-cream-soft' : 'text-ink';
  const barCls = onDark
    ? 'bg-transparent'
    : 'bg-cream/90 backdrop-blur-md border-b border-ink/10';

  const linkCls = `nav-link text-[11px] font-sans font-semibold tracking-[0.22em] uppercase ${colorCls}`;

  const LeftLinks = () => (
    <div className="hidden lg:flex items-center gap-10">
      <Link to="/" className={linkCls}>Home</Link>
      <a href="/#about" className={linkCls}>About</a>
      <DropdownMenu>
        <DropdownMenuTrigger className={`${linkCls} flex items-center gap-1 outline-none`}>
          Projects <ChevronDown size={14} strokeWidth={1.5} />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="bg-cream-soft border-ink/15 rounded-none min-w-[220px] p-2"
        >
          {projects.map((p) => (
            <DropdownMenuItem key={p.slug} asChild>
              <Link
                to={`/projects/${p.slug}`}
                className="text-ink font-serif text-[15px] tracking-wide py-2 px-3 cursor-pointer focus:bg-olive/10"
              >
                {p.title}
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  const RightLinks = () => (
    <div className="hidden lg:flex items-center gap-10">
      <a href="/#contact" className={linkCls}>Contact</a>
      <a href="/#more" className={linkCls}>More</a>
    </div>
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${barCls}`}
    >
      <div className="max-w-[1480px] mx-auto px-6 lg:px-12 h-[72px] grid grid-cols-[1fr_auto_1fr] items-center">
        <LeftLinks />

        <Link
          to="/"
          className={`font-display italic text-[22px] md:text-[26px] tracking-wide ${colorCls} text-center whitespace-nowrap`}
        >
          <span className="opacity-70 mr-1">.</span>
          {siteMeta.brand}
          <span className="opacity-70 ml-1">.</span>
        </Link>

        <div className="flex justify-end items-center">
          <RightLinks />

          {/* Mobile trigger */}
          <Sheet>
            <SheetTrigger
              className={`lg:hidden ${colorCls} p-2`}
              aria-label="Open menu"
            >
              <Menu size={22} strokeWidth={1.5} />
            </SheetTrigger>
            <SheetContent side="right" className="bg-cream text-ink border-l-ink/10 w-[86%] sm:w-[420px]">
              <div className="flex flex-col gap-2 pt-8">
                <Link to="/" className="font-display italic text-2xl mb-6">.{siteMeta.brand}.</Link>
                <Link to="/" className="nav-link text-sm tracking-[0.22em] uppercase py-3">Home</Link>
                <a href="/#about" className="nav-link text-sm tracking-[0.22em] uppercase py-3">About</a>
                <div className="pt-3 pb-1 text-xs tracking-[0.22em] uppercase text-ink/60">Projects</div>
                {projects.map((p) => (
                  <Link
                    key={p.slug}
                    to={`/projects/${p.slug}`}
                    className="font-serif text-lg py-1.5 text-ink hover:text-olive transition-colors"
                  >
                    {p.title}
                  </Link>
                ))}
                <a href="/#contact" className="nav-link text-sm tracking-[0.22em] uppercase py-3 mt-6">Contact</a>
                <a href="/#more" className="nav-link text-sm tracking-[0.22em] uppercase py-3">More</a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
